import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Public page required for Stripe Connect platforms. Stripe expects the
// platform to publish a clear "what we won't process" list so prospective
// hosts/restaurants/drivers know the boundaries before they apply, and so
// Stripe's risk team can point to it during onboarding review.
//
// We mirror Stripe's own Restricted Businesses list (linked at the bottom)
// and add Ariv-specific carve-outs for the verticals we operate.

const RestrictedBusinessesPage = () => {
  const sectionHeadingClass = 'text-xl md:text-2xl font-bold text-[#1F3A68] mt-8 mb-3';
  const paragraphClass = 'text-slate-700 leading-relaxed mb-3';
  const listClass = 'list-disc pl-6 space-y-1 text-slate-700 mb-3';

  return (
    <div className="min-h-screen bg-[#F4F2E9]">
      <Header />
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-10">
          <h1 className="text-3xl md:text-4xl font-bold text-[#1F3A68] mb-2">
            Restricted Businesses on Ariv
          </h1>
          <p className="text-sm text-slate-500 mb-8">
            Last updated: 13 May 2026
          </p>

          <section className="mb-8">
            <p className={paragraphClass}>
              Ariv is a marketplace operated by <strong>Brick &amp; Capital Ltd</strong>{' '}
              (company number{' '}
              <a
                href="https://find-and-update.company-information.service.gov.uk/company/16655788"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-[#1F3A68]"
              >
                16655788
              </a>
              ). We connect guests with hosts, landlords, restaurants, drivers, and service
              providers across London. Payments are processed through Stripe.
            </p>
            <p className={paragraphClass}>
              To keep the Platform safe and within the bounds of UK law and our payment
              processor's rules, the categories of business and activity below are not
              permitted on Ariv. Listings, accounts, and transactions involving these
              activities will be removed, and we reserve the right to terminate accounts
              that violate this policy.
            </p>
          </section>

          <h2 className={sectionHeadingClass}>1. Categories we do not host</h2>
          <p className={paragraphClass}>
            The following are prohibited across every vertical on Ariv (Stay, Eat, Ride,
            Fix, Invest):
          </p>

          <h3 className="text-base font-semibold text-[#1F3A68] mt-5 mb-2">
            Illegal or regulated goods and services
          </h3>
          <ul className={listClass}>
            <li>Controlled drugs or drug paraphernalia</li>
            <li>Firearms, ammunition, explosives, or weapons of any kind</li>
            <li>Counterfeit or unauthorised replica goods</li>
            <li>Items obtained through theft, fraud, or breach of intellectual property</li>
            <li>Wildlife products, endangered species, ivory</li>
          </ul>

          <h3 className="text-base font-semibold text-[#1F3A68] mt-5 mb-2">
            Adult, gambling, and other high-risk categories
          </h3>
          <ul className={listClass}>
            <li>Adult content, escort services, or any sexually-oriented service</li>
            <li>Gambling, lotteries, sports betting, or games of chance</li>
            <li>Cryptocurrency exchange or crypto-to-fiat conversion services</li>
            <li>Multi-level marketing or pyramid schemes</li>
            <li>Get-rich-quick schemes, binary options, unregistered investment offers</li>
          </ul>

          <h3 className="text-base font-semibold text-[#1F3A68] mt-5 mb-2">
            Financial services we are not authorised to host
          </h3>
          <ul className={listClass}>
            <li>Money transmission, money services businesses, or remittance</li>
            <li>Credit repair, debt consolidation, or debt-collection services</li>
            <li>Unregistered loan or credit products</li>
            <li>Payment aggregation or payment-facilitator activity</li>
          </ul>

          <h3 className="text-base font-semibold text-[#1F3A68] mt-5 mb-2">
            Other prohibited categories
          </h3>
          <ul className={listClass}>
            <li>Hate-based content, discrimination, or harassment of any group</li>
            <li>Misleading health claims, unauthorised medical advice or treatments</li>
            <li>Pharmaceuticals (prescription or otherwise) without proper licensing</li>
            <li>Door-to-door sales or unsolicited telemarketing</li>
            <li>Activities sanctioned under UK trade or financial sanctions regimes</li>
          </ul>

          <h2 className={sectionHeadingClass}>2. Vertical-specific restrictions</h2>

          <h3 className="text-base font-semibold text-[#1F3A68] mt-5 mb-2">
            Stay (short-term lets)
          </h3>
          <ul className={listClass}>
            <li>
              Greater London hosts letting an entire home must comply with the 90-night
              cap under the Deregulation Act 2015 unless they hold planning permission.
              We enforce this in the booking flow.
            </li>
            <li>
              The host must hold any required leasehold consent. We require an explicit
              declaration at onboarding.
            </li>
            <li>HMO and large-occupancy listings require the appropriate council licence.</li>
            <li>
              "Party houses" and venues marketed primarily for events, raves, or
              unlicensed parties.
            </li>
          </ul>

          <h3 className="text-base font-semibold text-[#1F3A68] mt-5 mb-2">
            Eat (restaurants and delivery)
          </h3>
          <ul className={listClass}>
            <li>
              FSA Food Hygiene rating of less than 2. Restaurants that drop below 2 are
              suspended automatically.
            </li>
            <li>
              Sale of alcohol without a valid premises licence. Alcohol-only menus are
              not supported.
            </li>
            <li>
              Live animals or unpasteurised products in categories where UK law prohibits
              direct-to-consumer sale.
            </li>
            <li>Misrepresented allergen information or absent allergen disclosures.</li>
          </ul>

          <h3 className="text-base font-semibold text-[#1F3A68] mt-5 mb-2">
            Ride (taxi)
          </h3>
          <ul className={listClass}>
            <li>
              Drivers without a current Transport for London Private Hire driver licence.
            </li>
            <li>
              Vehicles without current MOT, hire insurance, and TfL Private Hire plate.
            </li>
            <li>Cross-border journeys outside Greater London (Phase 1).</li>
          </ul>

          <h3 className="text-base font-semibold text-[#1F3A68] mt-5 mb-2">
            Fix (service providers)
          </h3>
          <ul className={listClass}>
            <li>Gas work without a Gas Safe Register number.</li>
            <li>
              Electrical work without NICEIC, NAPIT, or ELECSA certification at the
              appropriate level.
            </li>
            <li>
              Home-visit services without enhanced DBS certification refreshed in the
              last three years.
            </li>
            <li>
              Any tradesperson without active Public Liability insurance of at least
              £2,000,000.
            </li>
          </ul>

          <h2 className={sectionHeadingClass}>3. How we enforce this</h2>
          <p className={paragraphClass}>
            Every host, restaurant, driver, and service provider completes identity
            and required document checks at onboarding. We use the FSA's open data for
            restaurants, TfL's licence
            register for drivers, and direct document uploads for service providers. We
            re-check key documents on rolling expiry windows and auto-suspend accounts
            whose certificates lapse.
          </p>
          <p className={paragraphClass}>
            Where we discover a violation, we will remove the listing or account,
            cancel any in-flight transactions, and where appropriate report to the
            relevant authority. If you believe an account is operating in breach of
            this policy, please email <strong>support@ariv.co.uk</strong>.
          </p>

          <h2 className={sectionHeadingClass}>4. Underlying payment-processor rules</h2>
          <p className={paragraphClass}>
            Ariv processes payments through Stripe. Stripe's own restricted-business
            list applies in full and is incorporated into our Terms of Service. You can
            read Stripe's list here:{' '}
            <a
              href="https://stripe.com/legal/restricted-businesses"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-[#1F3A68]"
            >
              stripe.com/legal/restricted-businesses
            </a>
            .
          </p>

          <h2 className={sectionHeadingClass}>5. Changes</h2>
          <p className={paragraphClass}>
            We will update this page as the regulatory landscape changes and as we add
            new verticals to the Platform. Material changes will be communicated by
            email to affected partners with reasonable notice.
          </p>

          <div className="mt-10 pt-6 border-t border-slate-200 text-sm text-slate-500">
            <Link to="/terms" className="underline text-[#1F3A68]">
              Terms of Service
            </Link>{' · '}
            <Link to="/privacy" className="underline text-[#1F3A68]">
              Privacy Policy
            </Link>{' · '}
            <Link to="/host/payments-and-risk" className="underline text-[#1F3A68]">
              Payments and seller risk
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RestrictedBusinessesPage;
